import { AuthBrandLogo } from '../../features/auth/AuthBrandLogo';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrganizationBySlug, createJoinRequest } from '../../features/organizations/api';
import type { Organization } from '../../features/organizations/types';
import './OrganizationJoinPage.css';

export default function OrganizationJoinPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [requestedRole, setRequestedRole] = useState<'user' | 'admin'>('admin');

  useEffect(() => {
    if (!slug) {
      setError('組織IDが指定されていません');
      setIsLoading(false);
      return;
    }

    loadOrganization();
  }, [slug]);

  const loadOrganization = async () => {
    if (!slug) return;

    try {
      setIsLoading(true);
      setError('');
      const data = await getOrganizationBySlug(slug);
      setOrganization(data);
    } catch (err: any) {
      console.error('Failed to load organization:', err);
      if (err.response?.status === 404) {
        setError('組織が見つかりませんでした');
      } else {
        setError(err.message || '組織情報の読み込みに失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organization) return;

    setError('');
    setIsSubmitting(true);

    try {
      await createJoinRequest(organization.id, {
        message: message.trim() || undefined,
        requested_role: requestedRole,
      });

      // 成功後、サンクス画面へリダイレクト
      navigate('/organizations/join-requested', {
        replace: true,
        state: { organizationName: organization.display_name },
      });
    } catch (err: any) {
      console.error('Failed to create join request:', err);

      // エラーメッセージの処理
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        setError(errorData?.error || '参加依頼の送信に失敗しました');
      } else if (err.response?.status === 401) {
        setError('認証が必要です。ログインしてください。');
      } else {
        setError(err.message || '参加依頼の送信に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="loading-state">組織情報を読み込み中...</div>
        </div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <AuthBrandLogo />
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">エラー</h1>
            </div>
            <div className="auth-content">
              <p className="auth-intro">{error}</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <AuthBrandLogo />
        <div className="auth-card">
          {/* 組織情報ヘッダー */}
          <div className="org-header">
            {organization.logo_url && (
              <img
                src={organization.logo_url}
                alt={`${organization.display_name}のロゴ`}
                className="org-logo"
              />
            )}
            <h1 className="org-name">{organization.display_name}</h1>
            {organization.description && (
              <p className="org-description">{organization.description}</p>
            )}
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="auth-message auth-message-error">
              {error}
            </div>
          )}

          {/* 参加依頼フォーム */}
          <div className="auth-content">
              <form onSubmit={handleSubmit} className="join-form">
                <h2 className="section-title">希望する権限</h2>
                <p className="section-description">
                  承認時に管理者が最終的な権限を決定します。
                </p>
                <div className="auth-options">
                  <button
                    type="button"
                    className={`auth-option ${requestedRole === 'admin' ? 'selected' : ''}`}
                    onClick={() => setRequestedRole('admin')}
                  >
                    <div className="option-icon">
                      <img src="/user_multiple_male_female.svg" alt="管理者" />
                    </div>
                    <h3 className="option-title">管理者</h3>
                    <p className="option-description">
                    ユーザー管理、APIの新規接続、チャージ、プラン変更も含めた全機能を利用可能。
                    </p>
                  </button>

                  <button
                    type="button"
                    className={`auth-option ${requestedRole === 'user' ? 'selected' : ''}`}
                    onClick={() => setRequestedRole('user')}
                  >
                    <div className="option-icon">
                      <img src="/single_woman.svg" alt="ユーザー" />
                    </div>
                    <h3 className="option-title">ユーザー</h3>
                    <p className="option-description">
                      画像生成などの一般的なサービス利用。接続済みAPIを通じたデータ取得・更新は可能。
                    </p>
                  </button>
                </div>

                <h2 className="section-title message-section-title">メッセージ（任意）</h2>
                <p className="section-description">
                  管理者に送信されるメッセージです。参加理由などを記載すると承認されやすくなります。
                </p>
                <textarea
                  id="message"
                  className="form-textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="参加理由や自己紹介などを記入してください"
                  rows={4}
                  maxLength={500}
                />

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    戻る
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '送信中...' : '参加依頼を送信'}
                  </button>
                </div>
              </form>
          </div>
        </div>
      </div>
    </div>
  );
}
